import { useEffect, useRef, useState } from 'react';
import { ReportTableRow } from '../../../../utils/ReportUtils';

export interface IBulkEditBrowseViewSnapshot {
    searchKey: string;
    filterColumns: object[];
    sortColumns: object[];
}

export interface IBulkEditEnterPlan {
    /** Every currently listed row is in the selection — no rows to leave-animate. */
    matchesFullList: boolean;
    shouldAnimateLeave: boolean;
}

export interface IBulkEditPrepareEnterArgs {
    selectedCount: number;
    listedCount: number;
    leavingCount: number;
    viewSnapshot?: IBulkEditBrowseViewSnapshot;
}

export interface IBulkEditViewTransition {
    isActive: boolean;
    /** Browse host is still mounted; the pane is CSS-locked until commitEnter. */
    isEntering: boolean;
    editViewRows: ReportTableRow[];
    suppressSelectionSync: boolean;
    restoreSelectionAfterExit: Set<number> | null;
    animateNextRowBind: boolean;
    entryViewSnapshot: IBulkEditBrowseViewSnapshot | null;
    prepareEnter: (args: IBulkEditPrepareEnterArgs) => IBulkEditEnterPlan;
    commitEnter: (selectedRows: ReportTableRow[]) => void;
    beginExit: (selectedIds: Set<number>) => void;
    takeRestoreSelection: () => Set<number> | null;
    takeEntryViewSnapshot: () => IBulkEditBrowseViewSnapshot | null;
    clearSuppressSelectionSync: () => void;
    setAnimateNextRowBind: (value: boolean) => void;
    clearEnterTimers: () => void;
    scheduleCommitEnter: (selectedRows: ReportTableRow[], delayMs: number) => void;
}

export const useBulkEditViewTransition = (): IBulkEditViewTransition => {
    const [viewState, setViewState] = useState<{ isActive: boolean; isEntering: boolean; editViewRows: ReportTableRow[] }>({
        isActive: false,
        isEntering: false,
        editViewRows: [],
    });
    const isActive = viewState.isActive;
    const isEntering = viewState.isEntering;
    const editViewRows = viewState.editViewRows;

    const suppressSelectionSyncRef = useRef(false);
    const restoreSelectionAfterExitRef = useRef<Set<number> | null>(null);
    const animateNextRowBindRef = useRef(false);
    const entryViewSnapshotRef = useRef<IBulkEditBrowseViewSnapshot | null>(null);
    const enterTimeoutRef = useRef<number | null>(null);
    const enterRafRef = useRef<number | null>(null);

    const clearEnterTimers = () => {
        if (enterTimeoutRef.current != null) {
            window.clearTimeout(enterTimeoutRef.current);
            enterTimeoutRef.current = null;
        }
        if (enterRafRef.current != null) {
            window.cancelAnimationFrame(enterRafRef.current);
            enterRafRef.current = null;
        }
    };

    useEffect(() => () => clearEnterTimers(), []);

    const prepareEnter = (args: IBulkEditPrepareEnterArgs): IBulkEditEnterPlan => {
        suppressSelectionSyncRef.current = true;
        if (args.viewSnapshot) {
            entryViewSnapshotRef.current = args.viewSnapshot;
        }
        setViewState((current) => ({ ...current, isEntering: true }));
        return {
            matchesFullList: args.selectedCount === args.listedCount,
            shouldAnimateLeave: args.leavingCount > 0,
        };
    };

    const commitEnter = (selectedRows: ReportTableRow[]) => {
        clearEnterTimers();
        // The edit grid mounts fresh, so its rows fade/slide in through the shared rowDataBound handler.
        animateNextRowBindRef.current = true;
        setViewState({ isActive: true, isEntering: false, editViewRows: selectedRows });
    };

    const beginExit = (selectedIds: Set<number>) => {
        clearEnterTimers();
        restoreSelectionAfterExitRef.current = new Set(selectedIds);
        animateNextRowBindRef.current = true;
        setViewState({ isActive: false, isEntering: false, editViewRows: [] });
    };

    const takeRestoreSelection = (): Set<number> | null => {
        const ids = restoreSelectionAfterExitRef.current;
        restoreSelectionAfterExitRef.current = null;
        return ids;
    };

    const takeEntryViewSnapshot = (): IBulkEditBrowseViewSnapshot | null => {
        const snapshot = entryViewSnapshotRef.current;
        entryViewSnapshotRef.current = null;
        return snapshot;
    };

    const clearSuppressSelectionSync = () => {
        suppressSelectionSyncRef.current = false;
    };

    const setAnimateNextRowBind = (value: boolean) => {
        animateNextRowBindRef.current = value;
    };

    const scheduleCommitEnter = (selectedRows: ReportTableRow[], delayMs: number) => {
        clearEnterTimers();
        enterTimeoutRef.current = window.setTimeout(() => {
            enterTimeoutRef.current = null;
            commitEnter(selectedRows);
        }, delayMs);
    };

    return {
        isActive,
        isEntering,
        editViewRows,
        get suppressSelectionSync() {
            return suppressSelectionSyncRef.current;
        },
        get restoreSelectionAfterExit() {
            return restoreSelectionAfterExitRef.current;
        },
        get animateNextRowBind() {
            return animateNextRowBindRef.current;
        },
        get entryViewSnapshot() {
            return entryViewSnapshotRef.current;
        },
        prepareEnter,
        commitEnter,
        beginExit,
        takeRestoreSelection,
        takeEntryViewSnapshot,
        clearSuppressSelectionSync,
        setAnimateNextRowBind,
        clearEnterTimers,
        scheduleCommitEnter,
    };
};
